/**
 * Error Reporting Utility
 * Sends client-side errors to the backend for storage and debugging
 */

export interface ErrorReport {
  error_type: string;
  error_message: string;
  error_stack?: string;
  url?: string;
  user_agent?: string;
  context?: Record<string, any>;
  severity?: 'warning' | 'error' | 'critical';
}

/**
 * Reports an error to the backend error logging system
 */
export async function reportError(error: Error | ErrorEvent | PromiseRejectionEvent, additionalContext?: Record<string, any>): Promise<void> {
  try {
    // Extract error details based on error type
    let errorType: string;
    let errorMessage: string;
    let errorStack: string | undefined;

    if (error instanceof Error) {
      errorType = error.name || 'Error';
      errorMessage = error.message;
      errorStack = error.stack;
    } else if ('error' in error && error.error instanceof Error) {
      // ErrorEvent
      errorType = error.error.name || 'Error';
      errorMessage = error.error.message;
      errorStack = error.error.stack;
    } else if ('reason' in error) {
      // PromiseRejectionEvent
      const reason = error.reason;
      if (reason instanceof Error) {
        errorType = reason.name || 'UnhandledRejection';
        errorMessage = reason.message;
        errorStack = reason.stack;
      } else {
        errorType = 'UnhandledRejection';
        errorMessage = String(reason);
      }
    } else {
      errorType = 'UnknownError';
      errorMessage = String(error);
    }

    // Build error report
    const report: ErrorReport = {
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      context: additionalContext,
      severity: determineSeverity(errorType, errorMessage),
    };

    // Send to backend
    await fetch('/api/errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(report),
      // Don't throw on network errors - error reporting shouldn't break the app
    }).catch(() => {
      // Error report send failed - silent fail
    });
  } catch (reportingError) {
    // Silently fail - we don't want error reporting to cause more errors
  }
}

/**
 * Determines error severity based on type and message
 */
function determineSeverity(errorType: string, errorMessage: string): 'warning' | 'error' | 'critical' {
  // Critical errors that break core functionality
  const criticalPatterns = [
    /authentication/i,
    /authorization/i,
    /database/i,
    /network/i,
    /cannot read propert.*of undefined/i,
    /cannot read propert.*of null/i,
  ];

  // Warning-level errors that don't break functionality
  const warningPatterns = [
    /warning/i,
    /deprecated/i,
  ];

  const message = errorMessage.toLowerCase();

  if (criticalPatterns.some(pattern => pattern.test(message))) {
    return 'critical';
  }

  if (warningPatterns.some(pattern => pattern.test(message))) {
    return 'warning';
  }

  return 'error';
}

/**
 * Initializes global error handlers for unhandled errors and promise rejections
 * Should be called once on app initialization
 */
export function initializeErrorHandlers(): void {
  if (typeof window === 'undefined') {
    return; // Only run in browser
  }

  // Handle uncaught errors
  window.addEventListener('error', (event: ErrorEvent) => {
    // Uncaught error - report to backend
    reportError(event, {
      component: 'window',
      type: 'uncaught-error',
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // Unhandled rejection - report to backend
    reportError(event, {
      component: 'window',
      type: 'unhandled-rejection',
    });
  });

  // Global error handlers initialized
}
